import React, { useState, useEffect } from 'react';
import { FileText, Download, Filter } from 'lucide-react';
import { usePayroll } from '../hooks/usePayroll';
import { useEmployees } from '../hooks/useEmployees';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { generateReportPDF } from '../utils/reportGenerator';
import { usePayPeriodSettings } from '../hooks/usePayPeriodSettings';
import { parseLocalDateForDisplay } from '../utils/payrollPeriodCalculator';
import { supabase } from '../lib/supabase';
import type { PayrollEntry, PayPeriod } from '../types';

const Reports: React.FC = () => {
  const { payrollEntries, loading } = usePayroll();
  const { employees } = useEmployees();
  const { settings: payPeriodSettings, getPeriodNumber, calculateNextPayPeriods } = usePayPeriodSettings();
  const { settings: companySettings } = useCompanySettings();
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [selectedReport, setSelectedReport] = useState<'payroll' | 'tax' | 'deductions'>('payroll');
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [selectedQuarter, setSelectedQuarter] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [selectedEmployee, setSelectedEmployee] = useState<string>('all');
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf'>('csv');
  const [isGenerating, setIsGenerating] = useState(false);

  // Get available years from payroll entries based on pay dates
  const availableYears = React.useMemo(() => {
    const years = new Set(payrollEntries.map(entry =>
      entry.payDate ? parseLocalDateForDisplay(entry.payDate).getFullYear() : new Date().getFullYear()
    ));
    // Always include current year and 2026
    years.add(new Date().getFullYear());
    years.add(2026);
    return Array.from(years).sort((a, b) => b - a); // Sort descending
  }, [payrollEntries]);

  // Get available pay periods using the same logic as Process Payroll
  const availablePeriods = React.useMemo(() => {
    if (!payPeriodSettings) return [];
    
    // Get past periods (negative offset) and future periods (positive offset)
    const pastPeriods = calculateNextPayPeriods(8, -8); // 8 past periods
    const futurePeriods = calculateNextPayPeriods(4, 0); // 4 future periods
    
    // Combine and sort by start date, most recent first
    const allPeriods = [...pastPeriods, ...futurePeriods];
    return allPeriods.sort((a, b) => 
      parseLocalDateForDisplay(b.start).getTime() - parseLocalDateForDisplay(a.start).getTime()
    );
  }, [payPeriodSettings, calculateNextPayPeriods]);

  const getFormattedPeriod = (period: PayPeriod | PayrollEntry) => {
    if (!payPeriodSettings) return '';

    // Handle both PayPeriod and PayrollEntry types
    const startDate = 'payPeriod' in period
      ? parseLocalDateForDisplay(period.payPeriod.start)
      : parseLocalDateForDisplay(period.start);

    const periodNum = getPeriodNumber(startDate);
    return `Period ${periodNum}`;
  };

  const months = [
    { value: '0', label: 'January' },
    { value: '1', label: 'February' },
    { value: '2', label: 'March' },
    { value: '3', label: 'April' },
    { value: '4', label: 'May' },
    { value: '5', label: 'June' },
    { value: '6', label: 'July' },
    { value: '7', label: 'August' },
    { value: '8', label: 'September' },
    { value: '9', label: 'October' },
    { value: '10', label: 'November' },
    { value: '11', label: 'December' }
  ];

  const quarters = [
    { value: '1', label: 'Q1 (Jan-Mar)' },
    { value: '2', label: 'Q2 (Apr-Jun)' },
    { value: '3', label: 'Q3 (Jul-Sep)' },
    { value: '4', label: 'Q4 (Oct-Dec)' }
  ];

  // Debug logging - must be before any early returns
  useEffect(() => {
    const logDebugInfo = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('=== Reports Debug Info ===');
      console.log('Current user:', user?.email);
      console.log('Payroll Entries:', payrollEntries.length);
      console.log('Employees:', employees.length);
      console.log('Loading:', loading);

      if (payrollEntries.length > 0) {
        console.log('Sample payroll entry:', payrollEntries[0]);
      } else {
        console.warn('No payroll entries available for reports');
      }

      if (employees.length > 0) {
        console.log('Sample employee:', employees[0]);
      }
    };

    if (!loading) {
      logDebugInfo();
    }
  }, [payrollEntries, employees, loading]);

  const generateReport = async () => {
    let reportData = payrollEntries;

    // Filter by year based on pay date
    if (selectedYear !== 'all') {
      const year = parseInt(selectedYear);
      reportData = reportData.filter(entry =>
        entry.payDate ? parseLocalDateForDisplay(entry.payDate).getFullYear() === year : false
      );
    }

    // Filter by employee
    if (selectedEmployee !== 'all') {
      reportData = reportData.filter(entry => entry.employeeId === selectedEmployee);
    }

    // Filter by selected period
    if (selectedPeriod && selectedPeriod !== 'all') {
      const period = JSON.parse(selectedPeriod);
      reportData = reportData.filter(entry => 
        entry.payPeriod.start === period.start && 
        entry.payPeriod.end === period.end
      );
    }

    // Filter by month based on pay date
    if (selectedMonth !== 'all') {
      const currentYear = selectedYear === 'all' ? new Date().getFullYear() : parseInt(selectedYear);
      const monthNum = parseInt(selectedMonth);
      const startOfMonth = new Date(currentYear, monthNum, 1);
      const endOfMonth = new Date(currentYear, monthNum + 1, 0);

      reportData = reportData.filter(entry => {
        if (!entry.payDate) return false;
        const payDate = parseLocalDateForDisplay(entry.payDate);
        return payDate.getMonth() === monthNum &&
               payDate.getFullYear() === currentYear;
      });
    }

    // Filter by quarter based on pay date
    if (selectedQuarter !== 'all') {
      const quarterNum = parseInt(selectedQuarter);
      const year = selectedYear === 'all' ? new Date().getFullYear() : parseInt(selectedYear);
      const quarterStartMonth = (quarterNum - 1) * 3;
      const startOfQuarter = new Date(year, quarterStartMonth, 1);
      // Set end to the last moment of the last day of the quarter
      const endOfQuarter = new Date(year, quarterStartMonth + 3, 0, 23, 59, 59, 999);

      reportData = reportData.filter(entry => {
        if (!entry.payDate) return false;
        const payDate = parseLocalDateForDisplay(entry.payDate);

        // Include entry if the pay date falls within the quarter
        return (
          payDate >= startOfQuarter && payDate <= endOfQuarter
        );
      });
    }

    // Sort report data by pay period (descending) then by employee name
    reportData = reportData.sort((a, b) => {
      // First sort by pay period start date (most recent first)
      const dateA = parseLocalDateForDisplay(a.payPeriod.start);
      const dateB = parseLocalDateForDisplay(b.payPeriod.start);
      const dateCompare = dateB.getTime() - dateA.getTime();

      if (dateCompare !== 0) return dateCompare;

      // Then sort by employee name
      const employeeA = employees.find(e => e.id === a.employeeId);
      const employeeB = employees.find(e => e.id === b.employeeId);
      const nameA = employeeA ? `${employeeA.firstName} ${employeeA.lastName}` : '';
      const nameB = employeeB ? `${employeeB.firstName} ${employeeB.lastName}` : '';

      return nameA.localeCompare(nameB);
    });

    // Get period label for report header
    let periodLabel = 'All Periods';
    if (selectedPeriod && selectedPeriod !== 'all') {
      const period = JSON.parse(selectedPeriod);
      periodLabel = `${getFormattedPeriod(period)} (${parseLocalDateForDisplay(period.start).toLocaleDateString()} - ${parseLocalDateForDisplay(period.end).toLocaleDateString()})`;
    } else if (selectedMonth !== 'all') {
      periodLabel = `${months.find(m => m.value === selectedMonth)?.label} ${selectedYear === 'all' ? new Date().getFullYear() : selectedYear}`;
    } else if (selectedQuarter !== 'all') {
      periodLabel = `${quarters.find(q => q.value === selectedQuarter)?.label} ${selectedYear === 'all' ? new Date().getFullYear() : selectedYear}`;
    } else if (selectedYear !== 'all') {
      periodLabel = `Year ${selectedYear}`;
    }

    // Add employee name to period label if selected
    if (selectedEmployee !== 'all') {
      const employee = employees.find(e => e.id === selectedEmployee);
      if (employee) {
        periodLabel += ` - ${employee.firstName} ${employee.lastName}`;
      }
    }

    if (exportFormat === 'pdf') {
      setIsGenerating(true);
      await generateReportPDF(
        selectedReport,
        reportData,
        employees,
        periodLabel,
        (entry: PayrollEntry) => getFormattedPeriod(entry),
        companySettings
      );
      setIsGenerating(false);
      return;
    }

    // Calculate totals
    const totals = reportData.reduce((acc, entry) => ({
      grossPay: acc.grossPay + entry.grossPay,
      netPay: acc.netPay + entry.netPay,
      taxDeductions: acc.taxDeductions + entry.deductions.tax,
      insuranceDeductions: acc.insuranceDeductions + entry.deductions.insurance,
      retirementDeductions: acc.retirementDeductions + entry.deductions.retirement
    }), {
      grossPay: 0,
      netPay: 0,
      taxDeductions: 0,
      insuranceDeductions: 0,
      retirementDeductions: 0
    });

    // Format data based on report type
    let csvContent = '';
    switch (selectedReport) {
      case 'payroll':
        csvContent = 'Period,Employee,Pay Period,Pay Date,Gross Pay,Net Pay,Status\n';
        reportData.forEach(entry => {
          const employee = employees.find(e => e.id === entry.employeeId);
          const payDate = entry.payDate ? parseLocalDateForDisplay(entry.payDate).toLocaleDateString() : 'N/A';
          csvContent += `${getFormattedPeriod(entry)},${employee?.firstName} ${employee?.lastName},${parseLocalDateForDisplay(entry.payPeriod.start).toLocaleDateString()} to ${parseLocalDateForDisplay(entry.payPeriod.end).toLocaleDateString()},${payDate},${entry.grossPay},${entry.netPay},${entry.status}\n`;
        });
        break;
      case 'tax':
        // Group entries by employee for individual summaries
        const employeeSummaries = reportData.reduce((acc, entry) => {
          const employee = employees.find(e => e.id === entry.employeeId);
          if (!employee) return acc;
          
          if (!acc[employee.id]) {
            acc[employee.id] = {
              name: `${employee.firstName} ${employee.lastName}`,
              federalTax: 0,
              medicareTax: 0,
              socialSecurityTax: 0,
              stateTax: 0,
              grossPay: 0
            };
          }
          
          acc[employee.id].federalTax += entry.deductions.federalTax;
          acc[employee.id].medicareTax += entry.deductions.medicareTax;
          acc[employee.id].socialSecurityTax += entry.deductions.socialSecurityTax;
          acc[employee.id].stateTax += entry.deductions.stateTax;
          acc[employee.id].grossPay += entry.grossPay;
          
          return acc;
        }, {} as Record<string, {
          name: string;
          federalTax: number;
          medicareTax: number;
          socialSecurityTax: number;
          stateTax: number;
          grossPay: number;
        }>);

        // Calculate totals
        const totalFederalTax = Object.values(employeeSummaries).reduce((sum, emp) => sum + emp.federalTax, 0);
        const totalMedicareTax = Object.values(employeeSummaries).reduce((sum, emp) => sum + emp.medicareTax, 0);
        const totalSocialSecurityTax = Object.values(employeeSummaries).reduce((sum, emp) => sum + emp.socialSecurityTax, 0);
        const totalStateTax = Object.values(employeeSummaries).reduce((sum, emp) => sum + emp.stateTax, 0);
        const totalGrossPay = Object.values(employeeSummaries).reduce((sum, emp) => sum + emp.grossPay, 0);

        // Build CSV content with sections
        csvContent = 'Tax Report Summary\n\n';

        // 1. Individual Employee Summaries
        csvContent += 'Individual Employee Tax Summaries\n';
        csvContent += 'Employee,Gross Pay,State Tax,Federal Tax,Social Security Tax,Medicare Tax,Total Tax\n';
        Object.values(employeeSummaries).forEach(summary => {
          const totalTax = summary.stateTax + summary.federalTax + summary.socialSecurityTax + summary.medicareTax;
          csvContent += `${summary.name},${summary.grossPay.toFixed(2)},${summary.stateTax.toFixed(2)},${summary.federalTax.toFixed(2)},${summary.socialSecurityTax.toFixed(2)},${summary.medicareTax.toFixed(2)},${totalTax.toFixed(2)}\n`;
        });

        // 2. Employer Tax Liability
        csvContent += '\nEmployer Tax Liability Summary\n';
        csvContent += 'Category,Amount\n';
        csvContent += `Employer Social Security Tax,${totalSocialSecurityTax.toFixed(2)}\n`;
        csvContent += `Employer Medicare Tax,${totalMedicareTax.toFixed(2)}\n`;
        csvContent += `Total Employer Tax Liability,${(totalSocialSecurityTax + totalMedicareTax).toFixed(2)}\n`;

        // 3. Consolidated Tax Summary
        csvContent += '\nConsolidated Tax Summary\n';
        csvContent += 'Category,Amount\n';
        csvContent += `Total Gross Pay,${totalGrossPay.toFixed(2)}\n`;
        csvContent += `Total State Tax Due,${totalStateTax.toFixed(2)}\n`;
        csvContent += `Total Federal Income Tax Due,${totalFederalTax.toFixed(2)}\n`;
        csvContent += `Total Social Security Tax (Combined),${(totalSocialSecurityTax * 2).toFixed(2)}\n`;
        csvContent += `Total Medicare Tax (Combined),${(totalMedicareTax * 2).toFixed(2)}\n`;
        csvContent += `Grand Total Tax Due,${(
          totalStateTax +
          totalFederalTax +
          (totalSocialSecurityTax * 2) +
          (totalMedicareTax * 2)
        ).toFixed(2)}\n`;

        // 4. Detailed Transaction Records
        csvContent += '\nDetailed Transaction Records\n';
        csvContent += 'Period,Employee,Pay Period,Pay Date,Gross Pay,Federal Tax,Social Security Tax,Medicare Tax,State Tax,Total Tax,Tax Status\n';
        reportData.forEach(entry => {
          const employee = employees.find(e => e.id === entry.employeeId);
          const payDate = entry.payDate ? parseLocalDateForDisplay(entry.payDate).toLocaleDateString() : 'N/A';
          const totalTax = entry.deductions.federalTax + entry.deductions.socialSecurityTax + entry.deductions.medicareTax + entry.deductions.stateTax;
          csvContent += `${getFormattedPeriod(entry)},${employee?.firstName} ${employee?.lastName},${parseLocalDateForDisplay(entry.payPeriod.start).toLocaleDateString()} to ${parseLocalDateForDisplay(entry.payPeriod.end).toLocaleDateString()},${payDate},${entry.grossPay.toFixed(2)},${entry.deductions.federalTax.toFixed(2)},${entry.deductions.socialSecurityTax.toFixed(2)},${entry.deductions.medicareTax.toFixed(2)},${entry.deductions.stateTax.toFixed(2)},${totalTax.toFixed(2)},${employee?.taxStatus}\n`;
        });
        break;
      case 'deductions':
        csvContent = 'Period,Employee,Pay Period,Pay Date,Tax,Insurance,Retirement\n';
        reportData.forEach(entry => {
          const employee = employees.find(e => e.id === entry.employeeId);
          const payDate = entry.payDate ? parseLocalDateForDisplay(entry.payDate).toLocaleDateString() : 'N/A';
          csvContent += `${getFormattedPeriod(entry)},${employee?.firstName} ${employee?.lastName},${parseLocalDateForDisplay(entry.payPeriod.start).toLocaleDateString()} to ${parseLocalDateForDisplay(entry.payPeriod.end).toLocaleDateString()},${payDate},${entry.deductions.tax},${entry.deductions.insurance},${entry.deductions.retirement}\n`;
        });
        break;
    }

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedReport}-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading report data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Reports</h2>

      {payrollEntries.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">No Payroll Data Available</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>No payroll entries found. This could mean:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>No payroll has been processed yet</li>
                  <li>You don't have permission to view payroll data (admin access required)</li>
                  <li>There may be a database connection issue</li>
                </ul>
                <p className="mt-2">Check the browser console (F12) for detailed error information.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value);
                if (e.target.value === 'all') {
                  setSelectedMonth('all');
                  setSelectedQuarter('all');
                }
              }}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="all">All Years</option>
              {availableYears.map(year => (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Employee</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="all">All Employees</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.firstName} {employee.lastName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Report Type</label>
            <select
              value={selectedReport}
              onChange={(e) => setSelectedReport(e.target.value as 'payroll' | 'tax' | 'deductions')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="payroll">Payroll Summary</option>
              <option value="tax">Tax Report</option>
              <option value="deductions">Deductions Report</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Pay Period</label>
            <select
              value={selectedPeriod}
              onChange={(e) => {
                setSelectedPeriod(e.target.value);
              }}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="all">All Periods</option>
              {availablePeriods.map((period) => (
                <option 
                  key={`${period.start}-${period.end}`}
                  value={JSON.stringify(period)}
                >
                  {getFormattedPeriod(period)}
                  {' '}({parseLocalDateForDisplay(period.start).toLocaleDateString()} - {parseLocalDateForDisplay(period.end).toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                if (e.target.value !== 'all') {
                  setSelectedQuarter('all');
                }
              }}
              disabled={selectedYear === 'all'}
              title={selectedYear === 'all' ? 'Please select a specific year to filter by month' : ''}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
            >
              <option value="all">All Months</option>
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Quarter</label>
            <select
              value={selectedQuarter}
              onChange={(e) => {
                setSelectedQuarter(e.target.value);
                if (e.target.value !== 'all') {
                  setSelectedMonth('all');
                }
              }}
              disabled={selectedYear === 'all'}
              title={selectedYear === 'all' ? 'Please select a specific year to filter by quarter' : ''}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
            >
              <option value="all">All Quarters</option>
              {quarters.map(quarter => (
                <option key={quarter.value} value={quarter.value}>
                  {quarter.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Export Format</label>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as 'csv' | 'pdf')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="csv">CSV</option>
              <option value="pdf">PDF</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            onClick={() => {
              setSelectedPeriod('all');
              setSelectedMonth('all');
              setSelectedQuarter('all');
              setSelectedYear(new Date().getFullYear().toString());
              setSelectedEmployee('all');
            }}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Filter className="h-4 w-4" />
            Clear Filters
          </button>
          <button
            onClick={generateReport}
            disabled={isGenerating}
            className="flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Download className="h-4 w-4" />
            {isGenerating ? 'Generating...' : `Download ${exportFormat.toUpperCase()}`}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Reports</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {payrollEntries.slice(0, 5).map((entry) => {
            const employee = employees.find(e => e.id === entry.employeeId);
            return (
              <div key={entry.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <FileText className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {getFormattedPeriod(entry)} - {employee?.firstName} {employee?.lastName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {parseLocalDateForDisplay(entry.payPeriod.end).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 text-sm rounded-full ${
                  entry.status === 'paid' 
                    ? 'text-green-700 bg-green-100'
                    : entry.status === 'pending'
                    ? 'text-yellow-700 bg-yellow-100'
                    : 'text-blue-700 bg-blue-100'
                }`}>
                  {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Reports;